import { isTokenExpired } from "src/util";
import { saveCurrentEventPath } from "src/stores/EventDeepLinkSession";

const API_BASE_URL = '/api';

async function makeRequest(method: 'GET' | 'POST' | 'PUT', url: string, data?: any, isBlob?: boolean): Promise<any> {
  let token = localStorage.getItem('jwtToken');
  if (token && isTokenExpired(token)) {
    localStorage.removeItem('jwtToken');
    token = null;
    if (!shouldStayInLoginFlow(url)) {
      saveCurrentEventPath();
      window.location.href = '/';
      return Promise.reject('Token expired');
    }
  }

  const options: RequestInit = {
    method,
    headers: {
      ...getAuthHeader(token),
      'cache-control': 'no-cache'
    }
  };

  if (!isBlob) {
    options.headers = {
      ...options.headers,
      'Content-Type': 'application/json'
    };
  }

  if (method === 'GET' && data) {
    data.t = new Date().getTime();
    url = url + (url.includes('?') ? '&' : '?') + encodeParams(data);
  } else if ((method === 'POST' || method === 'PUT') && data) {
    options.body = JSON.stringify(data);
  }

  const response = await fetch(API_BASE_URL + url, options);
  persistRefreshedAuthToken(response, token);
  if (isBlob) {
    return handleBlobResponse(response);
  }
  return handleResponse(response);
}

const API = {
  get: async (url: string, params?: any): Promise<string> => {
    return makeRequest('GET', url, params);
  },
  post: async (url: string, data?: any): Promise<string> => {
    return makeRequest('POST', url, data);
  },
  put: async (url: string, data?: any): Promise<string> => {
    return makeRequest('PUT', url, data);
  },
  upload: async (url: string, formData: FormData): Promise<string> => {
    const token = localStorage.getItem('jwtToken');
    if (token && isTokenExpired(token)) {
      localStorage.removeItem('jwtToken');
      saveCurrentEventPath();
      window.location.href = '/';
      return Promise.reject('Token expired');
    }

    const response = await fetch(API_BASE_URL + url, {
      method: 'POST',
      headers: {
        ...getAuthHeader(token),
        'cache-control': 'no-cache'
      },
      body: formData
    });
    persistRefreshedAuthToken(response, token);
    return handleResponse(response);
  },
  getBlob: async (url: string, params?: any): Promise<Blob> => {
    return makeRequest('GET', url, params, true);
  },
  getDiscountRange: async (endDateTime: string): Promise<{ minDiscount: number, maxDiscount: number }> => {
    const response = await makeRequest('GET', '/discount-range', {
      endDateTime
    });
    try {
      return JSON.parse(response);
    } catch (e) {
      return { minDiscount: 5, maxDiscount: 25 };
    }
  }
};

export default API;

export const fetchAuthenticatedImage = async (url: string): Promise<string | null> => {
  const token = localStorage.getItem('jwtToken');
  if (!token) return null;
  try {
    const cacheBustedUrl = url + (url.includes('?') ? '&' : '?') + `t=${Date.now()}`;
    const res = await fetch(API_BASE_URL + cacheBustedUrl, {
      cache: 'no-store',
      headers: { Authorization: `Bearer ${token}` }
    });
    persistRefreshedAuthToken(res, token);
    if (!res.ok) return null;
    const blob = await res.blob();
    return window.URL.createObjectURL(blob);
  } catch {
    return null;
  }
};

function handleResponse(response: Response) {
  if (!response.ok) {
    return response.text().then((body) => {
      if (body) {
        try {
          const parsed = JSON.parse(body);
          // Domain-level 403 responses (for example FEATURE_DISABLED or a closed
          // cancellation window) are actionable in the current screen. Only an
          // unstructured 403 means the terminal session itself should be cleared.
          if (response.status === 403 && !parsed.code) {
            handleForbiddenSideEffects(response.url);
          }
          return Promise.reject({
            status: response.status,
            error: parsed.error || response.statusText,
            code: parsed.code,
            message: parsed.message || parsed.error || response.statusText
          });
        } catch {
          // Fall through to the text response below.
        }
      }

      if (response.status === 403) {
        handleForbiddenSideEffects(response.url);
      }

      return Promise.reject({
        status: response.status,
        error: response.statusText,
        message: body || response.statusText
      });
    });
  }

  return response.text();
}

function handleBlobResponse(response: Response) {
  if (response.status === 403) {
    return handleForbiddenResponse(response.url);
  }

  if (!response.ok) {
    return Promise.reject(new Error(`Failed to fetch Blob: ${response.statusText}`));
  }
  return response.blob();
}

function handleForbiddenResponse(responseUrl: string) {
  handleForbiddenSideEffects(responseUrl);
  return Promise.reject('Access forbidden - user may no longer exist');
}

function handleForbiddenSideEffects(responseUrl: string) {
  // Clear the token and redirect to main page to prevent loops.
  localStorage.removeItem('jwtToken');
  if (!shouldStayInLoginFlow(responseUrl.replace(API_BASE_URL, ''))) {
    saveCurrentEventPath();
    window.location.href = "/";
  }
}

function encodeParams(params: any) {
  return Object.keys(params).map((k: string) => encodeURIComponent(k) + '=' + encodeURIComponent(params[k])).join('&');
}

function getAuthHeader(token: string | null) {
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

export function persistRefreshedAuthToken(response: Response, requestToken: string | null) {
  const refreshedToken = response.headers.get('X-Refreshed-Auth-Token');
  if (refreshedToken && requestToken && localStorage.getItem('jwtToken') === requestToken) {
    localStorage.setItem('jwtToken', refreshedToken);
  }
}

function shouldStayInLoginFlow(url: string) {
  return isLoginRequest(url);
}

function isLoginRequest(url: string) {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    return pathname.startsWith('/login/') || pathname.startsWith('/api/login/');
  } catch (error) {
    return url.startsWith('/login/') || url.startsWith('/api/login/');
  }
}

export interface EarlyCheckinDiscount {
  name: string;
  value: number;
  type: string;
}

export interface StudioConfigsResponse {
  studioName: string;
  studioHomepageStore: string;
  studioHomepage: string;
  currencySymbol: string;
  appBaseUrl: string;
  checkInFutureDays?: number;
  serverDateTime?: string;
  emailAddress?: string;
  // Room rental configs
  rentFutureDays?: number;
  rent1hPrice?: number;
  rent1h30mPrice?: number;
  rent2hPrice?: number;
  rent2h30mPrice?: number;
  rent3hPrice?: number;
  rentEnabled?: boolean;
  roomRentalDescription?: string;
  classCancelCoins?: number;
  classCancelBeforeHours?: number;
  defaultTrainingPackageRecommendation?: string;
  packagesLowThresholdCoins?: number;
  packagesExpiringThresholdDays?: number;
  privateClassesEnabled?: boolean;
}

export const getEarlyCheckinDiscounts = (): Promise<EarlyCheckinDiscount[]> => {
  return API.get('/early-checkin-discounts').then(response => {
    try {
      return JSON.parse(response);
    } catch (e) {
      return [];
    }
  });
};

export const getStudioConfigs = (): Promise<StudioConfigsResponse> => {
  return API.get('/configs').then(response => {
    try {
      return JSON.parse(response);
    } catch (e) {
      return {
        studioName: 'Bachata Studio',
        studioHomepageStore: 'https://www.bachatastudio.ee',
        studioHomepage: 'https://www.bachatastudio.ee',
        currencySymbol: '€',
        appBaseUrl: 'https://app.bachatastudio.ee',
        checkInFutureDays: 7,
        serverDateTime: new Date().toISOString(),
        emailAddress: 'info@bachatastudio.ee',
        rentFutureDays: 14,
        rent1hPrice: 125,
        rent1h30mPrice: 165,
        rent2hPrice: 200,
        rent2h30mPrice: 225,
        rent3hPrice: 250,
        rentEnabled: false,
        roomRentalDescription: '',
        classCancelCoins: 30,
        classCancelBeforeHours: 4,
        defaultTrainingPackageRecommendation: 'S',
        packagesLowThresholdCoins: 250,
        packagesExpiringThresholdDays: 7,
        privateClassesEnabled: false
      };
    }
  });
};

export interface PackageCatalogItem {
  package_option_id: number;
  package_variation_id: number;
  name: string;
  subname?: string | null;
  variation_name: string;
  display_name: string;
  description?: string | null;
  coins_amount: number;
  private_coins_amount?: number;
  composition?: 'dance_only' | 'private_only' | 'mixed';
  validity_period_days?: number | null;
  renewal_interval?: 'monthly';
  price_eur: string;
  is_membership: boolean;
  is_private_class_package?: boolean;
  membership_cancel_after_days?: number;
  max_coins_rollover?: number;
  max_private_coins_rollover?: number;
  membership_auto_topup_coins_threshold?: number;
  membership_auto_topup_private_coins_threshold?: number;
  membership_auto_topup_days_threshold?: number;
  max_age_years_inclusive?: number | null;
}

export interface ActivePackageStatus {
  id: number;
  name: string;
  available_coins: number;
  end_date: string;
  package_type?: string;
  package_variation?: string;
  price_per_coin?: string;
}

export interface MembershipStatus {
  payment_card?: { brand: string; last4: string } | null;
  id: number;
  status: string;
  package_option_id: number;
  package_variation_id: number;
  tier_name: string;
  variation_name: string;
  monthly_coins: number;
  monthly_private_coins?: number;
  max_coins_rollover?: number;
  max_private_coins_rollover?: number;
  membership_auto_topup_coins_threshold?: number;
  membership_auto_topup_private_coins_threshold?: number;
  membership_auto_topup_days_threshold?: number;
  cancel_after_at?: string;
  cancel_allowed: boolean;
  next_billing_at?: string;
  renewal_interval?: 'monthly';
  billing_anchor_day?: number;
  pending_action?: string;
  pending_effective_at?: string;
  pending_tier_name?: string;
  pending_variation_name?: string;
}

export interface BulkCoinBucket {
  id: number;
  coins_amount: number;
  coins_remaining: number;
  expires_at?: string;
}

export interface PackagesCatalogResponse {
  training_packages: PackageCatalogItem[];
  memberships: PackageCatalogItem[];
}

export interface PackagesStatusResponse {
  manual_renewal_enabled?: boolean;
  user_id: number;
  active_package?: ActivePackageStatus | null;
  membership?: MembershipStatus | null;
  bulk_coin_buckets: BulkCoinBucket[];
  private_coin_balance?: number;
  private_coin_buckets?: PrivateCoinBucket[];
}

export interface CheckoutResponse {
  checkout_url: string;
  checkout_session_id: string;
}

const parseJson = <T,>(response: string, fallback: T): T => {
  try {
    return JSON.parse(response);
  } catch (e) {
    return fallback;
  }
};

// The catalog is student-specific: the backend removes free packages and youth packages
// for students whose known age makes them ineligible. An unknown age keeps both groups so
// the student can explicitly select youth pricing in the UI.
export const getPackagesCatalog = (userId: number): Promise<PackagesCatalogResponse> => {
  return API.get('/packages-membership/catalog', { userId }).then(response =>
    parseJson<PackagesCatalogResponse>(response, { training_packages: [], memberships: [] })
  );
};

export const getPackagesStatus = (userId: number): Promise<PackagesStatusResponse> => {
  return API.get('/packages-membership/status', { userId }).then(response =>
    parseJson<PackagesStatusResponse>(response, { user_id: userId, active_package: null, membership: null, bulk_coin_buckets: [] })
  );
};

export const createTrainingPackageCheckout = (userId: number, packageVariationId: number, requestId: string): Promise<CheckoutResponse> => {
  return API.post('/packages-membership/training-packages/checkout', {
    userId,
    packageVariationId,
    requestId
  }).then(response => parseJson<CheckoutResponse>(response, { checkout_url: '', checkout_session_id: '' }));
};

export const createMembershipCheckout = (userId: number, packageVariationId: number, requestId: string): Promise<CheckoutResponse> => {
  return API.post('/packages-membership/memberships/checkout', {
    userId,
    packageVariationId,
    requestId
  }).then(response => parseJson<CheckoutResponse>(response, { checkout_url: '', checkout_session_id: '' }));
};

export const createMembershipTopupCheckout = (userId: number, packageVariationId: number, requestId: string): Promise<CheckoutResponse> => {
  return API.post('/packages-membership/memberships/topup', {
    userId,
    packageVariationId,
    requestId
  }).then(response => parseJson<CheckoutResponse>(response, { checkout_url: '', checkout_session_id: '' }));
};

export interface MembershipRenewalPreview {
  request_id: string;
  confirmation_token: string;
  plan: string;
  amount_cents: number;
  currency: string;
  coins: { kind: 'dance' | 'private'; allocation: number; carried: number; forfeited: number; projected_balance: number }[];
  rollover_policy: string;
  rollover_notice: string;
  next_billing_at: string;
}

export interface MembershipRenewalOperation {
  request_id: string;
  status: 'not_found' | 'processing' | 'processed' | 'failed';
  message?: string;
  next_billing_at?: string;
}

const renewalJson = <T,>(response: string): T => JSON.parse(response);

export const previewMembershipRenewal = (userId: number): Promise<MembershipRenewalPreview> =>
  API.post('/packages-membership/memberships/renewal/preview', { userId }).then(renewalJson<MembershipRenewalPreview>);

export const confirmMembershipRenewal = (userId: number, preview: MembershipRenewalPreview): Promise<MembershipRenewalOperation> =>
  API.post('/packages-membership/memberships/renewal/confirm', {
    userId, requestId: preview.request_id, confirmationToken: preview.confirmation_token
  }).then(renewalJson<MembershipRenewalOperation>);

export const getMembershipRenewalStatus = (userId: number, requestId: string): Promise<MembershipRenewalOperation> =>
  API.get('/packages-membership/memberships/renewal/status', { userId, requestId }).then(renewalJson<MembershipRenewalOperation>);

export const createMembershipCardSetup = (userId: number): Promise<CheckoutResponse> => {
  return API.post('/packages-membership/memberships/card-setup', { userId })
    .then(response => parseJson<CheckoutResponse>(response, { checkout_url: '', checkout_session_id: '' }));
};

export const completeMembershipCardSetup = (userId: number, sessionId: string): Promise<{ updated: boolean }> => {
  return API.post('/packages-membership/memberships/card-setup/complete', { userId, sessionId })
    .then(response => parseJson(response, { updated: false }));
};

export const changeMembership = (userId: number, packageVariationId: number): Promise<PackagesStatusResponse> => {
  return API.post('/packages-membership/memberships/change', {
    userId,
    packageVariationId
  }).then(response => parseJson<any>(response, {}));
};

export const cancelMembership = (userId: number): Promise<PackagesStatusResponse> => {
  return API.post('/packages-membership/memberships/cancel', { userId }).then(response => parseJson<any>(response, {}));
};

export const keepMembershipActive = (userId: number): Promise<PackagesStatusResponse> => {
  return API.post('/packages-membership/memberships/keep-active', { userId }).then(response => parseJson<any>(response, {}));
};

export interface PrivateClassStyle {
  id: number;
  name: string;
  priority?: number | null;
  default?: boolean;
}

export interface PrivateClassTeacher {
  id: number;
  name: string;
  photo_url?: string | null;
  square_profile_photo_url?: string | null;
  private_class_description?: string | null;
  minimum_private_coin_price?: number | null;
  styles?: PrivateClassStyle[];
}

export interface PrivateClassPrice {
  persons_count: number;
  total_private_coins: number;
}

export interface PrivateClassService {
  id: number;
  teacher_id: number;
  name: string;
  duration_minutes: number;
  teacher_count: number;
  priority?: number | null;
  pricing_complete: boolean;
  prices: PrivateClassPrice[];
}

export interface PrivateClassVenue {
  id: number;
  name: string;
}

export interface PrivateClassSlot {
  starts_at: string;
  ends_at: string;
  teacher: PrivateClassTeacher;
  venue: PrivateClassVenue;
}

export interface PrivateCoinBucket {
  id: number;
  source_type: string;
  coins_amount: number;
  coins_remaining: number;
  expires_at?: string | null;
}

export interface PrivateClassBooking {
  id: number;
  booking_id: number;
  status: 'confirmed' | 'cancelled';
  teacher: PrivateClassTeacher;
  starts_at: string;
  ends_at: string;
  venue: PrivateClassVenue;
  room: { id: number; name: string };
  style: PrivateClassStyle;
  service: { id: number; name: string; duration_minutes: number };
  persons_count: number;
  cost: number;
  cancellable_until: string;
  can_cancel: boolean;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
  request_id: string;
}

export interface PrivateClassesOverviewResponse {
  booking_terms?: string;
  cancellation_fee?: number;
  user_id: number;
  private_coin_balance: number;
  private_coin_buckets: PrivateCoinBucket[];
  default_style_id?: number | null;
  upcoming_bookings: PrivateClassBooking[];
  packages_path: string;
}

export interface PrivateClassesStylesResponse {
  default_style_id?: number | null;
  styles: PrivateClassStyle[];
}

export interface PrivateClassesTeachersResponse {
  teachers: PrivateClassTeacher[];
}

export interface PrivateClassesServicesResponse {
  services: PrivateClassService[];
}

export interface PrivateClassAvailabilityDatesResponse {
  dates: string[];
}

export interface PrivateClassAvailabilitySlotsResponse {
  slots: PrivateClassSlot[];
}

export interface PrivateClassSelectionRequest {
  userId: number;
  teacherId: number;
  styleId: number;
  serviceId: number;
  startsAt: string;
  venueId: number;
  personsCount: number;
}

export interface PrivateClassQuoteResponse {
  user_id: number;
  teacher_id: number;
  style_id: number;
  service_id: number;
  starts_at: string;
  ends_at: string;
  venue_id: number;
  persons_count: number;
  cost: number;
  balance: number;
  sufficient_balance: boolean;
  resume_token: string;
}

export interface PrivateClassBookingResponse {
  booking: PrivateClassBooking;
}

export interface PrivateClassCancellationResponse extends PrivateClassBookingResponse {
  refunded_coins: number;
  balance: number;
}

export interface PrivateClassBookingsResponse {
  scope: 'upcoming' | 'history';
  bookings: PrivateClassBooking[];
}

export interface PrivateCoinHistoryEvent {
  id: string;
  event_type: string;
  type: string;
  direction: 'credit' | 'debit';
  amount: number;
  signed_amount: number;
  occurred_at: string;
  expires_at?: string | null;
  cancellation_reason?: string | null;
  private_class?: {
    starts_at: string;
    teacher_name: string;
    style_name: string;
    service_name: string;
    venue_name: string;
    room_name: string;
    persons_count: number;
  };
}

export interface PrivateCoinHistoryResponse {
  events: PrivateCoinHistoryEvent[];
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
}

export const newRequestId = (prefix = 'request'): string => {
  if (window.crypto && 'randomUUID' in window.crypto) {
    return window.crypto.randomUUID();
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const getPrivateClassesOverview = (userId: number): Promise<PrivateClassesOverviewResponse> =>
  API.get('/private-classes/overview', { userId }).then(response => parseJson(response, {
    user_id: userId,
    private_coin_balance: 0,
    private_coin_buckets: [],
    default_style_id: null,
    upcoming_bookings: [],
    packages_path: '/packages-membership'
  }));

export const getPrivateClassStyles = (userId: number): Promise<PrivateClassesStylesResponse> =>
  API.get('/private-classes/styles', { userId }).then(response => parseJson(response, {
    default_style_id: null,
    styles: []
  }));

export const getPrivateClassTeachers = (userId: number, styleId?: number): Promise<PrivateClassesTeachersResponse> =>
  API.get('/private-classes/teachers', styleId == null ? { userId } : { userId, styleId })
    .then(response => parseJson(response, { teachers: [] }));

export const getPrivateClassServices = (userId: number, teacherId?: number): Promise<PrivateClassesServicesResponse> =>
  API.get('/private-classes/services', teacherId == null ? { userId } : { userId, teacherId })
    .then(response => parseJson(response, { services: [] }));

export const getPrivateClassAvailabilityDates = (
  userId: number,
  teacherIds: number[],
  serviceId: number,
  startDate?: string,
  endDate?: string,
  venueIds?: number[]
): Promise<PrivateClassAvailabilityDatesResponse> => {
  const params: any = { userId, teacherIds, serviceId };
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (venueIds?.length) params.venueIds = venueIds;
  return API.get('/private-classes/availability/dates', params)
    .then(response => parseJson(response, { dates: [] }));
};

export const getPrivateClassAvailabilitySlots = (
  userId: number,
  teacherIds: number[],
  serviceId: number,
  date: string,
  venueIds?: number[]
): Promise<PrivateClassAvailabilitySlotsResponse> => {
  const params: any = { userId, teacherIds, serviceId, date };
  if (venueIds?.length) params.venueIds = venueIds;
  return API.get('/private-classes/availability/slots', params)
    .then(response => parseJson(response, { slots: [] }));
};

export const postPrivateClassQuote = (request: PrivateClassSelectionRequest): Promise<PrivateClassQuoteResponse> =>
  API.post('/private-classes/quotes', request).then(response => parseJson(response, {
    user_id: request.userId,
    teacher_id: request.teacherId,
    style_id: request.styleId,
    service_id: request.serviceId,
    starts_at: request.startsAt,
    ends_at: '',
    venue_id: request.venueId,
    persons_count: request.personsCount,
    cost: 0,
    balance: 0,
    sufficient_balance: false,
    resume_token: ''
  }));

export const postPrivateClassBooking = (
  request: PrivateClassSelectionRequest,
  expectedCost: number,
  requestId = newRequestId('private-class')
): Promise<PrivateClassBookingResponse> =>
  API.post('/private-classes/bookings', { ...request, expectedCost, requestId })
    .then(response => parseJson(response, { booking: null as any }));

export const cancelPrivateClassBooking = (
  userId: number,
  bookingId: number,
  reason?: string
): Promise<PrivateClassCancellationResponse> =>
  API.post(`/private-classes/bookings/${bookingId}/cancel`, { userId, reason })
    .then(response => parseJson(response, { booking: null as any, refunded_coins: 0, balance: 0 }));

export const getPrivateClassBookings = (
  userId: number,
  scope: 'upcoming' | 'history'
): Promise<PrivateClassBookingsResponse> =>
  API.get('/private-classes/bookings', { userId, scope })
    .then(response => parseJson(response, { scope, bookings: [] }));

export const getPrivateCoinsHistory = (
  userId: number,
  page = 1,
  perPage = 25
): Promise<PrivateCoinHistoryResponse> =>
  API.get('/private-coins/history', { userId, page, perPage })
    .then(response => parseJson(response, {
      events: [],
      pagination: { page, per_page: perPage, total_count: 0, total_pages: 0 }
    }));

export interface Venue {
  id: number;
  name: string;
  street?: string;
  city?: string;
}

export interface Room {
  id: number;
  venueId: number;
  name: string;
}

export const getRentalVenues = (): Promise<Venue[]> => {
  return API.get('/rental/venues').then(response => {
    try {
      return JSON.parse(response);
    } catch (e) {
      return [];
    }
  });
};

export const getRentalRooms = (venueId?: number): Promise<Room[]> => {
  const url = venueId ? `/rental/locations?venueId=${venueId}` : '/rental/locations';
  return API.get(url).then(response => {
    try {
      return JSON.parse(response);
    } catch (e) {
      return [];
    }
  });
};

export interface RentalTimeSlot {
  locationId: number;
  locationName: string;
  startTime: string;
  endTime: string;
  displayText: string;
}

export const getAvailableRentalTimeSlots = (
  venueId: number,
  dayOfWeek: number,
  durationMinutes: number,
  date: Date
): Promise<RentalTimeSlot[]> => {
  const dateStr = date.toISOString();
  const url = `/rental/time-slots?venueId=${venueId}&dayOfWeek=${dayOfWeek}&durationMinutes=${durationMinutes}&date=${encodeURIComponent(dateStr)}`;
  return API.get(url).then(response => {
    try {
      return JSON.parse(response);
    } catch (e) {
      return [];
    }
  });
};

export interface RentalBookingRequest {
  userId: number;
  locationId: number;
  sellingItemId?: number;  // Optional: if using database selling items
  rentalType?: string;     // Type of rental (e.g., "1h", "2h") - used if sellingItemId is null
  durationMinutes?: number; // Duration in minutes - used if sellingItemId is null
  startTime: string;
  endTime: string;
  date: string;
}

export interface RentalBookingResponse {
  rentId: number;
  bookingId: number;
  cost: number;
  price: number;
}

export const bookRental = (request: RentalBookingRequest): Promise<RentalBookingResponse> => {
  return API.post('/rental/book', request).then(response => {
    try {
      return JSON.parse(response);
    } catch (e) {
      throw new Error('Failed to book rental');
    }
  });
};

export interface UpcomingRental {
  startTime: string;
  endTime: string;
  locationName: string;
  venueName: string;
  venueStreet?: string;
  venueCity?: string;
  price: number;
}

export const getUserUpcomingRentals = (userId: number): Promise<UpcomingRental[]> => {
  return API.get(`/rental/upcoming?userId=${userId}`).then(response => {
    try {
      return JSON.parse(response);
    } catch (e) {
      return [];
    }
  });
};

export interface CoinRentalRoom {
  id: number;
  name: string;
  size?: number;
  imageUrl?: string;
  venueName?: string;
  minCoins30min: number;
}

export interface CoinRentType {
  id: string;
  name: string;
  priority?: number;
  description?: string;
}

export interface CoinRentalTimeSlot {
  start: string;
  end: string;
  available: boolean;
  coins30min: number;
}

export interface CoinRentalBookingRequest {
  userId: number;
  locationId: number;
  date: string;
  rentTypeId: string;
  slots: string[];
  requestId: string;
}

export interface CoinRentalBookingResponse {
  rentId: number;
  bookingId: number;
  totalCoins: number;
  remainingCoins: number;
  locationId: number;
  locationName: string;
  rentTypeId: string;
  rentTypeName: string;
  date: string;
  slots: string[];
}

export const getCoinRentalRooms = (date: string): Promise<CoinRentalRoom[]> => {
  return API.get('/rental/coins/rooms', { date }).then(response => {
    const parsed = JSON.parse(response);
    return (parsed.rooms || []).map((room: any) => ({
      id: room.id,
      name: room.name,
      size: room.size,
      imageUrl: room.image_url,
      venueName: room.venue_name,
      minCoins30min: room.min_coins_30min
    }));
  });
};

export const getCoinRentalTypes = (
  date: string,
  locationId?: number
): Promise<CoinRentType[]> => {
  return API.get(
    '/rental/coins/rent-types',
    locationId == null ? { date } : { date, locationId }
  ).then(response => {
    const parsed = JSON.parse(response);
    return (parsed.rent_types || []).map((rentType: any) => ({
      id: rentType.id,
      name: rentType.name,
      priority: rentType.priority,
      description: rentType.description
    }));
  });
};

export const getCoinRentalAvailability = (
  date: string,
  locationId: number,
  rentTypeId: string
): Promise<CoinRentalTimeSlot[]> => {
  return API.get('/rental/coins/availability', {
    date,
    locationId,
    rentTypeId
  }).then(response => {
    const parsed = JSON.parse(response);
    return (parsed.slots || []).map((slot: any) => ({
      start: slot.start,
      end: slot.end,
      available: slot.available,
      coins30min: slot.coins_30min
    }));
  });
};

export const bookCoinRental = (
  request: CoinRentalBookingRequest
): Promise<CoinRentalBookingResponse> => {
  return API.post('/rental/coins/bookings', request).then(response => {
    const parsed = JSON.parse(response);
    return {
      rentId: parsed.rent_id,
      bookingId: parsed.booking_id,
      totalCoins: parsed.total_coins,
      remainingCoins: parsed.remaining_coins,
      locationId: parsed.location_id,
      locationName: parsed.location_name,
      rentTypeId: parsed.rent_type_id,
      rentTypeName: parsed.rent_type_name,
      date: parsed.date,
      slots: parsed.slots || []
    };
  });
};

export interface CancelCheckinRequest {
  trainingPeriodId: number;
  classTime: string;
  userId: number;
}

export interface CancelCheckinResponse {
  success: boolean;
  penaltyCoins: number;
  classCoins: number;
}

export const cancelCheckin = (request: CancelCheckinRequest): Promise<CancelCheckinResponse> => {
  return API.post('/cancel-checkin', request).then(response => {
    try {
      return JSON.parse(response);
    } catch (e) {
      throw new Error('Failed to cancel check-in');
    }
  });
};

export interface StudentProgress {
  levelProgressList: LevelProgress[];
  attendanceStats: AttendanceStats;
}

export interface LevelProgress {
  promotion: LevelPromotion;
  syllabusTopics: SyllabusTopic[];
  historySyllabusTopics?: SyllabusTopic[];
  currentSyllabusTopics?: SyllabusTopic[];
  totalTopics: number;
  learnedTopics: number;
  progressPercentage: number;
  // History (graduated level) progress
  historyTotalTopics?: number;
  historyLearnedTopics?: number;
  historyProgressPercentage?: number;
}

export interface LevelPromotion {
  assignmentId: number;
  levelId: number;
  styleId: number;
  styleName: string;
  levelNumber: number;
  levelName: string;
  levelColor: string;
  levelDescription: string;
  achievedAt: string;
  minimumAttendance?: number;
  recommendedAttendance?: number;
  nextLevelId?: number;
  nextLevelName?: string;
  nextLevelColor?: string;
}

export interface SyllabusTopic {
  id: number;
  styleId: number;
  topicName: string;
  topicDescription?: string;
  videoLink?: string;
  topicOrder: number;
  isLearned: boolean;
}

export interface StreakMilestone {
  weekThreshold: number;
  message: string;
  achieved: boolean;
  rewardName?: string | null;
}

export type StreakRewardStatus = 'available' | 'viewed' | 'redeemed' | 'expired';

export interface StreakReward {
  id: number;
  milestoneWeek: number;
  streak?: number;
  milestoneMessage?: string | null;
  rewardName: string;
  rewardDescription?: string | null;
  achievedAt: string;
  expiresAt?: string | null;
  redeemedAt?: string | null;
  viewedAt?: string | null;
  status: StreakRewardStatus;
}

export interface AttendanceStats {
  totalClassesAttended: number;
  currentStreak: number;
  longestStreak: number;
  attendanceByMonth: { [key: string]: number };
  attendanceByStyle: { [key: string]: number };
  milestones: StreakMilestone[];
  streakRewards: StreakReward[];
  nextMilestoneWeeks: number | null;
  nextMilestoneName: string | null;
  attendedWeeks: { [key: string]: number };
  streakAtRisk: boolean;
  streakInfoMessage: string | null;
  streakResetWeeks: number | null;
  currentWeekMessage: string | null;
  lastAttendedDate: string | null;
}

export const getStudentProgress = (userId: number): Promise<StudentProgress> => {
  return API.get(`/student-progress/${userId}`).then(response => {
    try {
      return JSON.parse(response);
    } catch (e) {
      throw new Error('Failed to fetch student progress');
    }
  });
};

export const markStreakRewardViewed = (rewardId: number, userId: number): Promise<void> => {
  return API.post(`/streak-rewards/${rewardId}/viewed`, { userId }).then(() => undefined);
};

export interface LeaderboardEntry {
  rank: number;
  userId: number;
  firstName: string;
  avatarUrl: string | null;
  classCount: number;
  currentUser: boolean;
}

export interface LeaderboardGroup {
  styleId: number;
  levelId: number | null;
  name: string;
}

export interface Leaderboard {
  availableGroups?: LeaderboardGroup[];
  groupName: string;
  groupStyleId: number | null;
  groupLevelId: number | null;
  lookbackDays: number;
  topStudents: LeaderboardEntry[];
  currentUserEntry: LeaderboardEntry | null;
  belowCurrentUser: LeaderboardEntry[];
  currentUserRank: number | null;
  currentUserClassCount: number;
  classesToTop: number;
  totalInGroup: number;
  ctaClassesNeeded: number | null;
  ctaTargetTopN: number | null;
}

export const getLeaderboard = (userId: number, styleId?: number): Promise<Leaderboard | null> => {
  return API.get(`/leaderboard/${userId}${styleId == null ? '' : `?styleId=${styleId}`}`).then(response => {
    if (!response) {
      return null;
    }
    try {
      return JSON.parse(response);
    } catch (e) {
      return null;
    }
  });
};

export interface CheckinSuggestion {
  trainingPeriodId: number;
  trainingId: number;
  trainingTitle: string;
  teachers: string;
  date: string; // ISO local date in Europe/Tallinn, e.g. "2026-08-16"
  hour: number;
  minute: number;
  duration: number;
  originalCoins: number;
  discountedCoins: number;
  savingCoins: number;
  discountValue: number; // actual discount coins stored as discount_amount; coins deducted = originalCoins - discountValue
  discountRuleId: number;
  venueId: number;
  venueName: string;
}

export const getCheckinSuggestions = (userId: number): Promise<CheckinSuggestion[]> => {
  return API.get(`/checkin-suggestions?userId=${userId}`).then(response => {
    if (!response) {
      return [];
    }
    try {
      return JSON.parse(response);
    } catch (e) {
      return [];
    }
  });
};
